import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { lightTheme } from "../../styles/theme";
import PageSection from "../PageSection";
import type { UserContactFilter } from "../../hooks/useUserListFilterState";

interface UsersStatsBarProps {
  totalUsers: number;
  mobileUsers: number;
  emailUsers: number;
  activeFilter: UserContactFilter;
  onFilterClick: (filter: UserContactFilter) => void;
}

function StatBox({
  value,
  label,
  active,
  onClick,
}: {
  value: number;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        textAlign: "center",
        cursor: "pointer",
        borderRadius: 2,
        p: 1,
        transition: "all 0.2s ease",
        backgroundColor: active
          ? alpha(theme.palette.primary.main, 0.05)
          : "transparent",
        "&:hover": {
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
          transform: "translateY(-2px)",
        },
      }}
      onClick={onClick}
    >
      <Typography
        variant="h3"
        sx={{
          color: lightTheme.colors.primary,
          fontWeight: 700,
          mb: 0.5,
          fontSize: { xs: "1.85rem", md: "2.5rem" },
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: "text.secondary",
          fontWeight: 500,
          letterSpacing: "0.5px",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function StatDivider() {
  return (
    <Box
      sx={{
        height: { xs: 50, md: 60 },
        width: 2,
        background: `linear-gradient(180deg, transparent 0%, ${lightTheme.colors.primary}40 50%, transparent 100%)`,
        borderRadius: 1,
      }}
    />
  );
}

export const UsersStatsBar: React.FC<UsersStatsBarProps> = ({
  totalUsers,
  mobileUsers,
  emailUsers,
  activeFilter,
  onFilterClick,
}) => (
  <PageSection>
    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          backgroundColor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: { xs: 2, md: 5 },
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <StatBox
            value={totalUsers}
            label="Total Users"
            active={activeFilter === "all"}
            onClick={() => onFilterClick("all")}
          />
          <StatDivider />
          <StatBox
            value={mobileUsers}
            label="Mobile Users"
            active={activeFilter === "mobile"}
            onClick={() => onFilterClick("mobile")}
          />
          <StatDivider />
          <StatBox
            value={emailUsers}
            label="Email Users"
            active={activeFilter === "email"}
            onClick={() => onFilterClick("email")}
          />
        </Box>
      </Paper>
    </Box>
  </PageSection>
);

export default UsersStatsBar;
